import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KewagBadge } from "@/components/common/KewagBadge";
import { Loader2, AlertCircle, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";

// FUNDIO LOGIN — blue-black / anthracite + real bright gold. Content differences only (Eyebrow + Claim).
const LOGO_SRC = "/fundio-logo-dark.png"; // GOLD + WHITE master logo (dark backgrounds only)

const inputClass =
  "w-full rounded-lg border border-fundio-line bg-fundio-surface px-4 text-sm text-fundio-cream outline-none transition-colors placeholder:text-fundio-slate focus:border-fundio-gold-bright focus:ring-1 focus:ring-fundio-gold-bright/40";

export default function Login() {
  const { user, login, setupTwoFactor, verifyTwoFactor, changePassword } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [phase, setPhase] = useState("credentials"); // credentials | changepw | twofa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 2FA state (pre-auth token kept in memory only — never localStorage)
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [challenge, setChallenge] = useState(null); // setup_2fa | 2fa
  const [qr, setQr] = useState(null);
  const [manualKey, setManualKey] = useState(null);
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [recoverCode, setRecoverCode] = useState(""); // owner self-recovery (2FA-gated)

  useEffect(() => {
    if (user && user !== false) navigate("/", { replace: true });
  }, [user, navigate]);

  const resetToCredentials = () => {
    setPhase("credentials");
    setPreAuthToken(null);
    setChallenge(null);
    setQr(null);
    setManualKey(null);
    setCode("");
    setNewPw("");
    setConfirmPw("");
    setRecoverCode("");
    setError("");
  };

  // Owner self-service recovery: prove ownership with a current TOTP code, then the server
  // returns a single-use link token straight to THIS browser; we navigate to set a password.
  const submitRecovery = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/owner-recovery/start", {
        email: email.trim(),
        code: recoverCode,
      });
      setLoading(false);
      navigate(`/passwort-setzen?token=${encodeURIComponent(data.token)}`);
    } catch (err) {
      setLoading(false);
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setRecoverCode("");
    }
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    if (res.ok) {
      setLoading(false);
      navigate("/", { replace: true });
      return;
    }
    if (res.twoFactor) {
      setPreAuthToken(res.preAuthToken);
      setChallenge(res.challenge);
      setPassword("");
      if (res.challenge === "change_password") {
        setPhase("changepw");
        setLoading(false);
        return;
      }
      if (res.challenge === "setup_2fa") {
        const s = await setupTwoFactor(res.preAuthToken);
        if (s.ok) {
          setQr(s.qr);
          setManualKey(s.manualKey);
        } else {
          setError(s.error);
        }
      }
      setPhase("twofa");
      setLoading(false);
      return;
    }
    setLoading(false);
    setError(res.error);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await verifyTwoFactor(preAuthToken, code, challenge);
    setLoading(false);
    if (res.ok) {
      navigate("/", { replace: true });
    } else {
      setError(res.error);
      setCode("");
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 10) { setError(t("login.minPwError")); return; }
    if (newPw !== confirmPw) { setError(t("login.pwMismatch")); return; }
    setLoading(true);
    const res = await changePassword(preAuthToken, newPw);
    if (!res.ok) { setLoading(false); setError(res.error); return; }
    setPreAuthToken(res.preAuthToken);
    setChallenge(res.challenge);
    setNewPw(""); setConfirmPw("");
    if (res.challenge === "setup_2fa") {
      const s = await setupTwoFactor(res.preAuthToken);
      if (s.ok) { setQr(s.qr); setManualKey(s.manualKey); } else { setError(s.error); }
    }
    setPhase("twofa");
    setLoading(false);
  };

  const isSetup = challenge === "setup_2fa";

  return (
    <div className="dark min-h-screen w-full bg-fundio-dark text-fundio-cream lg:grid lg:grid-cols-2">
      {/* HERO — 50% (Desktop only) — neutral, dark, cool. Photo = atmosphere only. */}
      <div className="relative hidden overflow-hidden bg-fundio-dark lg:block">
        <img
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
          src="/login-hero.png"
          draggable="false"
          style={{ filter: "grayscale(1) brightness(0.7) contrast(1.06)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(9,13,18,0.72) 0%, rgba(14,20,27,0.85) 55%, rgba(22,29,37,0.95) 100%)",
          }}
        />
        <div className="absolute left-0 right-0 flex justify-center" style={{ top: 46 }}>
          <img
            src={LOGO_SRC}
            alt="Fundio"
            draggable="false"
            data-testid="login-logo"
            className="object-contain"
            style={{ width: 240, height: "auto" }}
          />
        </div>
        <div className="fundio-reveal absolute" style={{ left: 48, bottom: 70, maxWidth: 470 }}>
          <p
            className="font-sans text-fundio-gold-bright"
            style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.20em" }}
          >
            {t("login.eyebrow")}
          </p>
          <h2
            className="font-heading tracking-tight text-fundio-cream"
            style={{ marginTop: 18, fontSize: 46, fontWeight: 800, lineHeight: 0.98 }}
          >
            {t("login.claim")}
          </h2>
          <p
            className="font-sans text-fundio-mist"
            style={{ marginTop: 20, fontSize: 15, fontWeight: 400, lineHeight: 1.55, maxWidth: 450 }}
          >
            {t("login.heroDesc")}
          </p>
        </div>
      </div>

      {/* LOGIN — 50% — true blue-black / charcoal */}
      <div className="relative flex min-h-screen items-center justify-center bg-fundio-dark px-6">
        <div className="fundio-reveal" style={{ width: 352, maxWidth: "calc(100vw - 48px)" }}>
          <div className="mb-8 flex justify-center lg:hidden">
            <img
              src={LOGO_SRC}
              alt="Fundio"
              draggable="false"
              data-testid="login-logo-mobile"
              className="object-contain"
              style={{ width: 190, height: "auto" }}
            />
          </div>

          <p
            className="font-sans text-fundio-gold-bright"
            style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em" }}
          >
            {t("login.eyebrow")}
          </p>

          {phase === "credentials" ? (
            <form onSubmit={submitCredentials} data-testid="login-form">
              <h1 className="font-heading text-fundio-cream" style={{ marginTop: 12, fontSize: 30, fontWeight: 800, lineHeight: 1.05 }}>
                {t("login.signInTitle")}
              </h1>
              <p className="font-sans text-fundio-mist" style={{ marginTop: 6, fontSize: 14, fontWeight: 400 }}>
                {t("login.signInSubtitle")}
              </p>

              <label
                className="block font-sans text-fundio-mist"
                style={{ marginTop: 24, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}
              >
                {t("login.email")}
              </label>
              <input
                data-testid="login-email-input"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@fundio.ch"
                autoComplete="email"
                className={inputClass}
                style={{ height: 48, marginTop: 6 }}
              />

              <label
                className="block font-sans text-fundio-mist"
                style={{ marginTop: 16, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}
              >
                {t("login.password")}
              </label>
              <input
                data-testid="login-password-input"
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className={inputClass}
                style={{ height: 48, marginTop: 6 }}
              />

              {error && (
                <div
                  data-testid="login-error"
                  className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300"
                  style={{ marginTop: 16 }}
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-fundio-gold bg-fundio-surface font-sans text-fundio-cream shadow-sm transition-all hover:border-fundio-gold-highlight hover:shadow-[0_0_18px_-4px_rgba(231,181,21,0.5)] disabled:opacity-60"
                style={{ height: 48, marginTop: 24, fontSize: 14, fontWeight: 500 }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.signIn")}
              </button>

              <button
                type="button"
                data-testid="owner-recovery-link"
                onClick={() => { setError(""); setPhase("recover"); }}
                className="mt-4 inline-flex items-center gap-1.5 font-sans text-fundio-slate transition-colors hover:text-fundio-mist"
                style={{ fontSize: 12 }}
              >
                <KeyRound className="h-3.5 w-3.5" /> Owner-Zugang wiederherstellen
              </button>
            </form>
          ) : phase === "recover" ? (
            <form onSubmit={submitRecovery} data-testid="owner-recover-panel">
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-fundio-gold/40 bg-fundio-gold-bright/10 px-3 py-1 text-fundio-gold-bright" style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em" }}>
                <KeyRound className="h-3.5 w-3.5" /> OWNER-WIEDERHERSTELLUNG
              </div>
              <h1 className="font-heading text-fundio-cream" style={{ marginTop: 14, fontSize: 26, fontWeight: 800, lineHeight: 1.08 }}>
                Zugang wiederherstellen
              </h1>
              <p className="font-sans text-fundio-mist" style={{ marginTop: 6, fontSize: 13, fontWeight: 400, lineHeight: 1.5 }}>
                Bestätigen Sie mit Ihrer E-Mail und dem aktuellen 6-stelligen Code aus Ihrer Authenticator-App.
                Danach legen Sie Ihr neues Passwort selbst fest. Ihre 2FA bleibt unverändert.
              </p>
              <label className="block font-sans text-fundio-mist" style={{ marginTop: 20, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {t("login.email")}
              </label>
              <input data-testid="owner-recover-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@fundio.ch" autoComplete="email" className={inputClass} style={{ height: 48, marginTop: 6 }} />
              <label className="block font-sans text-fundio-mist" style={{ marginTop: 16, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {t("login.sixDigitCode")}
              </label>
              <input data-testid="owner-recover-code-input" required inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={recoverCode} onChange={(e) => setRecoverCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className={`${inputClass} text-center font-mono tracking-[0.4em]`} style={{ height: 48, marginTop: 6, fontSize: 18 }} />
              {error && (
                <div data-testid="owner-recover-error" className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300" style={{ marginTop: 16 }}>
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}
              <button type="submit" data-testid="owner-recover-submit-button" disabled={loading || recoverCode.length !== 6 || !email.trim()}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-fundio-gold bg-fundio-surface font-sans text-fundio-cream shadow-sm transition-all hover:border-fundio-gold-highlight hover:shadow-[0_0_18px_-4px_rgba(231,181,21,0.5)] disabled:opacity-40"
                style={{ height: 48, marginTop: 20, fontSize: 14, fontWeight: 500 }}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Weiter – Passwort festlegen"}
              </button>
              <button type="button" data-testid="owner-recover-back-button" onClick={resetToCredentials}
                className="mt-4 inline-flex items-center gap-1.5 font-sans text-fundio-slate transition-colors hover:text-fundio-mist" style={{ fontSize: 12 }}>
                <ArrowLeft className="h-3.5 w-3.5" /> {t("login.backToLogin")}
              </button>
            </form>
          ) : phase === "changepw" ? (
            <form onSubmit={submitNewPassword} data-testid="changepw-panel">
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-fundio-gold/40 bg-fundio-gold-bright/10 px-3 py-1 text-fundio-gold-bright" style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em" }}>
                <KeyRound className="h-3.5 w-3.5" /> {t("login.firstLogin")}
              </div>
              <h1 className="font-heading text-fundio-cream" style={{ marginTop: 14, fontSize: 26, fontWeight: 800, lineHeight: 1.08 }}>
                {t("login.setNewPassword")}
              </h1>
              <p className="font-sans text-fundio-mist" style={{ marginTop: 6, fontSize: 13, fontWeight: 400, lineHeight: 1.5 }}>
                {t("login.setNewPasswordDesc")}
              </p>
              <label className="block font-sans text-fundio-mist" style={{ marginTop: 20, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {t("login.newPassword")}
              </label>
              <input data-testid="changepw-new-input" required type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="••••••••••" autoComplete="new-password" className={inputClass} style={{ height: 48, marginTop: 6 }} />
              <label className="block font-sans text-fundio-mist" style={{ marginTop: 16, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {t("login.confirmPassword")}
              </label>
              <input data-testid="changepw-confirm-input" required type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="••••••••••" autoComplete="new-password" className={inputClass} style={{ height: 48, marginTop: 6 }} />
              {error && (
                <div data-testid="changepw-error" className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300" style={{ marginTop: 16 }}>
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}
              <button type="submit" data-testid="changepw-submit-button" disabled={loading || newPw.length < 10 || confirmPw.length < 10}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-fundio-gold bg-fundio-surface font-sans text-fundio-cream shadow-sm transition-all hover:border-fundio-gold-highlight hover:shadow-[0_0_18px_-4px_rgba(231,181,21,0.5)] disabled:opacity-40"
                style={{ height: 48, marginTop: 20, fontSize: 14, fontWeight: 500 }}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.continueTo2fa")}
              </button>
              <button type="button" data-testid="changepw-back-button" onClick={resetToCredentials}
                className="mt-4 inline-flex items-center gap-1.5 font-sans text-fundio-slate transition-colors hover:text-fundio-mist" style={{ fontSize: 12 }}>
                <ArrowLeft className="h-3.5 w-3.5" /> {t("login.backToLogin")}
              </button>
            </form>
          ) : (
            <form onSubmit={submitCode} data-testid="twofa-panel">
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-fundio-gold/40 bg-fundio-gold-bright/10 px-3 py-1 text-fundio-gold-bright" style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em" }}>
                <KeyRound className="h-3.5 w-3.5" /> {t("login.twofa")}
              </div>
              <h1 className="font-heading text-fundio-cream" style={{ marginTop: 14, fontSize: 26, fontWeight: 800, lineHeight: 1.08 }}>
                {isSetup ? t("login.setup2faTitle") : t("login.verifyCodeTitle")}
              </h1>
              <p className="font-sans text-fundio-mist" style={{ marginTop: 6, fontSize: 13, fontWeight: 400, lineHeight: 1.5 }}>
                {isSetup ? t("login.setup2faDesc") : t("login.verify2faDesc")}
              </p>

              {isSetup && (
                <div className="mt-5 rounded-2xl border border-fundio-line bg-fundio-surface p-4">
                  {qr ? (
                    <img
                      src={qr}
                      alt={t("login.qrAlt")}
                      data-testid="twofa-qr"
                      className="mx-auto rounded-lg bg-white p-2"
                      style={{ width: 184, height: 184 }}
                    />
                  ) : (
                    <div className="flex h-[184px] items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-fundio-gold-bright" />
                    </div>
                  )}
                  {manualKey && (
                    <div className="mt-3 text-center">
                      <p className="font-sans text-fundio-slate" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        {t("login.manualKey")}
                      </p>
                      <code
                        data-testid="twofa-manual-key"
                        className="mt-1 block select-all break-all font-mono text-fundio-cream"
                        style={{ fontSize: 12 }}
                      >
                        {manualKey}
                      </code>
                    </div>
                  )}
                </div>
              )}

              <label
                className="block font-sans text-fundio-mist"
                style={{ marginTop: 20, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}
              >
                {t("login.sixDigitCode")}
              </label>
              <input
                data-testid="twofa-code-input"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className={`${inputClass} text-center font-mono tracking-[0.4em]`}
                style={{ height: 48, marginTop: 6, fontSize: 18 }}
              />

              {error && (
                <div
                  data-testid="twofa-error"
                  className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300"
                  style={{ marginTop: 16 }}
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="twofa-submit-button"
                disabled={loading || code.length !== 6}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-fundio-gold bg-fundio-surface font-sans text-fundio-cream shadow-sm transition-all hover:border-fundio-gold-highlight hover:shadow-[0_0_18px_-4px_rgba(231,181,21,0.5)] disabled:opacity-40"
                style={{ height: 48, marginTop: 20, fontSize: 14, fontWeight: 500 }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isSetup ? t("login.activate2fa") : t("login.signIn")}
              </button>

              <button
                type="button"
                data-testid="twofa-back-button"
                onClick={resetToCredentials}
                className="mt-4 inline-flex items-center gap-1.5 font-sans text-fundio-slate transition-colors hover:text-fundio-mist"
                style={{ fontSize: 12 }}
              >
                <ArrowLeft className="h-3.5 w-3.5" /> {t("login.backToLogin")}
              </button>
            </form>
          )}

          <p className="flex items-center gap-1.5 font-sans text-fundio-slate" style={{ marginTop: 24, fontSize: 12 }}>
            <ShieldCheck className="h-3.5 w-3.5 text-fundio-gold-bright" /> {t("login.secureNote")}
          </p>
        </div>
      </div>

      <KewagBadge />
    </div>
  );
}
